import React from 'react';
import { X, Pill, Package, DollarSign, ShoppingCart } from 'lucide-react';

const MedicamentDetailsModal = ({ medicament, isOpen, onClose }) => {
  if (!isOpen || !medicament) return null;

  const getCategoryColor = (category) => {
    const colors = {
      'Antibiotique': 'bg-blue-100 text-blue-800',
      'Antalgique': 'bg-purple-100 text-purple-800',
      'Anti-inflammatoire': 'bg-indigo-100 text-indigo-800',
      'Antihistaminique': 'bg-cyan-100 text-cyan-800',
      'Antiviral': 'bg-green-100 text-green-800',
      'Antifongique': 'bg-yellow-100 text-yellow-800',
      'Vitamines': 'bg-orange-100 text-orange-800',
      'Autre': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['Autre'];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-full">
              <Pill className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{medicament.name}</h2>
              {medicament.genericName && (
                <p className="text-gray-600">{medicament.genericName}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Informations générales */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Forme galénique</p>
                <p className="text-sm font-medium text-gray-900">{medicament.dosageForm}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Dosage</p>
                <p className="text-sm font-medium text-gray-900">{medicament.strength} {medicament.unit}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Catégorie</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(medicament.category)}`}>
                  {medicament.category}
                </span>
              </div>
              {medicament.manufacturer && (
                <div>
                  <p className="text-sm text-gray-600">Laboratoire</p>
                  <p className="text-sm font-medium text-gray-900">{medicament.manufacturer}</p>
                </div>
              )}
            </div>
          </div>

          {/* Stock et prix */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Stock et tarifs</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Package className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Stock</p>
                  <p className="text-2xl font-bold text-gray-900">{medicament.stockQuantity}</p>
                </div>
              </div>
              {medicament.price && (
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Prix unitaire</p>
                    <p className="text-2xl font-bold text-gray-900">${medicament.price}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Stock minimum</p>
                <p className="text-sm font-medium text-gray-900">{medicament.minStockLevel}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Stock maximum</p>
                <p className="text-sm font-medium text-gray-900">{medicament.maxStockLevel}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          {medicament.description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-sm text-gray-600">{medicament.description}</p>
            </div>
          )}

          {/* Statuts */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statuts</h3>
            <div className="flex flex-wrap gap-2">
              {medicament.requiresPrescription && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  Prescription requise
                </span>
              )}
              {medicament.isActive ? (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Actif
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  Inactif
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MedicamentDetailsModal;

